import Papa from 'papaparse'
import type { ParseResult } from 'papaparse'
import { determinePapaConfig } from '../../../View/Page/OrderManagementPage/FileHandling/CSVHelper.ts'
import { readXlsxRows } from './xlsxReader.ts'

export type ParsedSheet = {
  headers: string[]
  rows: Record<string, string>[]
  /** What the reader had to guess (encoding, delimiter), for showing next to the sheet. */
  notes: string[]
}

/** More rows than any plate-based order; keeps the stored draft bounded. */
export const MAX_SHEET_ROWS = 2000

const fromTable = (table: string[][]): ParsedSheet => {
  const [headerRow = [], ...body] = table
  const headers = headerRow.map((header, i) => header.trim() || `Column ${i + 1}`)
  const rows = body
    .slice(0, MAX_SHEET_ROWS)
    .map((cells) => Object.fromEntries(headers.map((header, i) => [header, (cells[i] ?? '').trim()])))
  const notes = body.length > MAX_SHEET_ROWS ? [`Only the first ${MAX_SHEET_ROWS} rows are used.`] : []
  return { headers, rows, notes }
}

/** CSV and TSV through the classic import's encoding and delimiter detection, without its confirm dialog. */
const readDelimited = async (file: File): Promise<ParsedSheet> =>
  new Promise((resolve, reject) => {
    determinePapaConfig(file, (result: ParseResult<unknown>) => {
      const table = [
        result.meta.fields ?? [],
        ...(result.data as Record<string, string>[]).map((row) => (result.meta.fields ?? []).map((f) => String(row[f] ?? '')))
      ]
      resolve(fromTable(table))
    }).then((config) => {
      if (!config.options) {
        reject(new Error(config.errors.join(', ') || 'The sheet could not be read'))
        return
      }
      Papa.parse(file, config.options.config)
    }, reject)
  })

export const readSheet = async (file: File): Promise<ParsedSheet> => {
  if (/\.xlsx$/i.test(file.name)) {
    return fromTable(await readXlsxRows(file))
  }
  if (/\.xls$/i.test(file.name)) {
    throw new Error('Old .xls workbooks cannot be read — save the sheet as .xlsx or .csv')
  }
  return readDelimited(file)
}
